<template>
  <div ref="footerRef" class="footer-wrap" :class="{ 'is-visible': isVisible }">
    <footer class="container footer-shell">
      <div class="footer-brand">
        <span class="brand-mark">Mitikuin</span>
        <p class="brand-copy mb-0">Diseno web que convierte visitas en oportunidades reales.</p>
      </div>

      <nav class="footer-nav" aria-label="Enlaces de Mitikuin">
        <a
          href="https://blog.mitikuin.com/"
          target="_blank"
          rel="noopener"
          class="footer-link"
        >
          Blog
        </a>
      </nav>

      <ul class="social-list" aria-label="Redes sociales">
        <li>
          <a href="https://www.instagram.com/Mitikuin/" target="_blank" rel="noopener" aria-label="Instagram" class="social-btn">
            <svg fill="currentColor" height="23" width="23" viewBox="-271 273 256 256">
              <path d="M-64.5,273h-157c-27.3,0-49.5,22.2-49.5,49.5v157c0,27.3,22.2,49.5,49.5,49.5h157c27.3,0,49.5-22.2,49.5-49.5v-157 C-15.1,295.2-37.3,273-64.5,273z M-179.6,374.7c8.2-11.3,21.5-18.8,36.5-18.8s28.3,7.4,36.5,18.8c5.4,7.4,8.5,16.5,8.5,26.3 c0,24.8-20.2,45.1-45.1,45.1s-44.9-20.3-44.9-45.1C-188.1,391.2-184.9,382.1-179.6,374.7z"/>
            </svg>
          </a>
        </li>

        <li>
          <a href="https://www.facebook.com/Mitikuin" target="_blank" rel="noopener" aria-label="Facebook" class="social-btn">
            <svg fill="currentColor" height="23" width="23" viewBox="-337 273 123.5 256">
              <path d="M-260.9,327.8c0-10.3,9.2-14,19.5-14c10.3,0,21.3,3.2,21.3,3.2l6.6-39.2c0,0-14-4.8-47.4-4.8c-20.5,0-32.4,7.8-41.1,19.3 c-8.2,10.9-8.5,28.4-8.5,39.7v25.7H-337V396h26.5v133h49.6V396h39.3l2.9-38.3h-42.2V327.8z"/>
            </svg>
          </a>
        </li>

        <li>
          <a href="https://www.linkedin.com/company/mitikuin" target="_blank" rel="noopener" aria-label="LinkedIn" class="social-btn">
            <svg fill="currentColor" height="26" width="26" viewBox="0 0 448 512">
              <path d="M100.28 448H7.4V149.1h92.88zm-46.44-338C24.07 110 0 85.94 0 56.72A53.28 53.28 0 0 1 53.84 3.44a53.28 53.28 0 0 1 53.84 53.28c0 29.22-24.07 53.28-53.84 53.28zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.25-79.2-48.25 0-55.65 37.7-55.65 76.6V448h-92.68V149.1h88.98v40.8h1.3c12.4-23.6 42.64-48.25 87.76-48.25 93.8 0 111.1 61.8 111.1 142.3V448z"/>
            </svg>
          </a>
        </li>
      </ul>
    </footer>

    <div class="container footer-meta">
      <p class="copyright mb-0">© {{ currentYear }} Mitikuin</p>
      <p class="recaptcha-legal mb-0">
        Este sitio está protegido por reCAPTCHA y se aplican la
        <a href="https://policies.google.com/privacy" class="meta-link" target="_blank" rel="noopener">Política de Privacidad</a>
        y los
        <a href="https://policies.google.com/terms" class="meta-link" target="_blank" rel="noopener">Términos del Servicio</a>
        de Google.
      </p>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'

const footerRef = ref(null)
const isVisible = ref(false)
let observer = null

const currentYear = new Date().getFullYear()

onMounted(() => {
  if (!footerRef.value) return

  observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries
      if (entry.isIntersecting) {
        isVisible.value = true
        observer?.disconnect()
        observer = null
      }
    },
    { threshold: 0.2 }
  )

  observer.observe(footerRef.value)
})

onUnmounted(() => {
  observer?.disconnect()
  observer = null
})
</script>

<style scoped>
.footer-wrap {
  position: relative;
  width: 100%;
  padding-top: 1.25rem;
  background:
    radial-gradient(900px 220px at 15% 0%, rgba(0, 229, 255, 0.16), transparent 60%),
    linear-gradient(180deg, #091420, #08121b 70%, #070f16);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.footer-shell,
.footer-meta {
  opacity: 0;
  transform: translateY(24px);
}

.footer-wrap.is-visible .footer-shell,
.footer-wrap.is-visible .footer-meta {
  opacity: 1;
  transform: translateY(0);
}

.footer-shell {
  display: grid;
  grid-template-columns: 1.2fr auto auto;
  align-items: center;
  gap: 1rem;
  padding-top: 1rem;
  padding-bottom: 1rem;
  transition: opacity 0.65s ease, transform 0.65s ease;
}

.footer-brand {
  text-align: left;
}

.brand-mark {
  display: inline-block;
  color: #ecfbff;
  font-weight: 800;
  letter-spacing: 0.03em;
  font-size: 1.2rem;
}

.brand-copy {
  margin-top: 0.35rem;
  color: rgba(233, 247, 255, 0.78);
  font-size: 0.95rem;
  max-width: 430px;
}

.footer-nav {
  display: flex;
  align-items: center;
  justify-content: center;
}

.footer-link {
  text-decoration: none;
  color: #eaf8ff;
  font-weight: 700;
  padding: 0.45rem 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
}

.footer-link:hover {
  transform: translateY(-1px);
  background: rgba(168, 255, 120, 0.14);
  border-color: rgba(168, 255, 120, 0.45);
  color: #f7fffd;
}

.social-list {
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.65rem;
  margin: 0;
  padding: 0;
}

.social-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  color: #eefcff;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.06);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.social-btn:hover {
  transform: translateY(-2px);
  border-color: rgba(0, 229, 255, 0.5);
  box-shadow: 0 0 0 4px rgba(0, 229, 255, 0.12);
}

.footer-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding-top: 0.8rem;
  padding-bottom: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  transition: opacity 0.75s ease 0.12s, transform 0.75s ease 0.12s;
}

.copyright {
  color: rgba(236, 251, 255, 0.74);
  font-size: 0.9rem;
}

.recaptcha-legal {
  color: rgba(236, 251, 255, 0.74);
  font-size: 0.82rem;
  line-height: 1.45;
  text-align: right;
}

.meta-link {
  color: #ddf8ff;
}

.meta-link:hover {
  color: #a8ff78;
}

@media (max-width: 992px) {
  .footer-shell {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .footer-brand {
    text-align: center;
  }

  .brand-copy {
    margin-left: auto;
    margin-right: auto;
  }

  .social-list {
    justify-content: center;
  }

  .footer-meta {
    justify-content: center;
  }

  .recaptcha-legal {
    text-align: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .footer-shell,
  .footer-meta {
    opacity: 1;
    transform: none;
    transition: none;
  }

  .footer-link,
  .social-btn {
    transition: none;
  }
}
</style>
